import React, { useEffect, useState } from 'react';
import { Typography, Box, Avatar, Grid, Chip, Divider } from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { ClientDetails } from '../../types/client';
import { BaseDialog } from '../common/BaseDialog.tsx';
import { getDefaultPermissions } from '../../services/access-control.service';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';

interface ClientDetailsModalProps {
  client: ClientDetails;
  open: boolean;
  onClose: () => void;
}

const DetailItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body1">{value || 'Не вказано'}</Typography>
  </Grid>
);

export const ClientDetailsModal: React.FC<ClientDetailsModalProps> = ({ client, open, onClose }) => {
  const g = client.general;
  const [moduleLabels, setModuleLabels] = useState<Record<string, string>>({});
  const [permissionLabels, setPermissionLabels] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    getDefaultPermissions().then(schema => {
      setModuleLabels(Object.fromEntries(schema.map(m => [m.code, m.label])));
      setPermissionLabels(Object.fromEntries(
        [...new Map(schema.flatMap(m => m.permissions).map(p => [p.code, p.label])).entries()]
      ));
    }).catch(() => {});
  }, [open]);

  const title = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Avatar src={g.avatar || undefined} sx={{ width: 40, height: 40 }}>
        {g.name?.charAt(0)}
      </Avatar>
      <Box>
        <Typography variant="h6" lineHeight={1.2}>{g.name} {g.surname}</Typography>
        <Typography variant="body2" color="text.secondary">{g.email}</Typography>
      </Box>
    </Box>
  );

  const activePermissions = client.accessPermissions?.filter(p => p.permissions.length > 0) ?? [];

  return (
    <BaseDialog open={open} onClose={onClose} title={title} maxWidth="md" data-i-doc={ModalIndices.CLIENT_DETAILS}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>Групи клієнта</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {client.groups && client.groups.length > 0 ? (
          client.groups.map(group => (
            <Chip
              key={group.id}
              label={group.name}
              icon={<LocalOfferIcon sx={{ fontSize: '14px !important', color: 'inherit !important' }} />}
              sx={{
                bgcolor: group.color,
                color: '#fff',
                '& .MuiChip-icon': { color: '#fff' }
              }}
              size="small"
            />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">Груп не призначено</Typography>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Загальна інформація</Typography>
      <Grid container spacing={2}>
        <DetailItem label="Ім'я" value={g.name} />
        <DetailItem label="Прізвище" value={g.surname} />
        <DetailItem label="По батькові" value={g.patronymic} />
        <DetailItem label="Телефон" value={g.phone} />
        <DetailItem label="Рік народження" value={g.bornYear} />
        <DetailItem label="Стать" value={g.gender === 1 ? 'Чоловіча' : g.gender === 2 ? 'Жіноча' : null} />
        <DetailItem label="Зріст (см)" value={g.height} />
        <DetailItem label="Рівень активності" value={g.activityLevel} />
        <DetailItem label="Ціль" value={g.goal} />
        <DetailItem label="Часовий пояс" value={g.timezone} />
      </Grid>

      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Надані доступи</Typography>
      {activePermissions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Доступів не надано</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {activePermissions.map(p => (
            p.permissions.map(type => (
              <Chip
                key={`${p.moduleCode}-${type}`}
                icon={<VpnKeyIcon />}
                label={`${moduleLabels[p.moduleCode] ?? p.moduleCode}: ${permissionLabels[type] ?? type}`}
                size="small"
                variant="outlined"
                color={type === 'write' ? 'primary' : 'default'}
              />
            ))
          ))}
        </Box>
      )}
    </BaseDialog>
  );
};
